import React from 'react';
import { Box, Button, Chip, Divider, Switch, Typography } from '@mui/material';
import { Coins } from 'lucide-react';
import type { TokenPack } from '../../services/tokenPack.service';
import { formatAdminNumber } from '../../utils/numberFormat';

interface TokenPackCardProps {
  tokenPack: TokenPack;
  onToggleStatus: (publicId: string, isActive: boolean) => void;
  onEdit: (publicId: string) => void;
}

export const TokenPackCard: React.FC<TokenPackCardProps> = ({
  tokenPack,
  onToggleStatus,
  onEdit,
}) => {
  const formatPrice = (price: number, currency: string) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: currency === 'VND' ? 0 : 2,
    }).format(price);

  return (
    <Box className="h-full rounded-xl bg-white shadow-sm transition-all hover:shadow-md dark:bg-slate-800">
      <Box className="p-6">
        <Box className="mb-4 flex items-start justify-between">
          <Box className="flex min-w-0 items-center gap-3">
            <Box className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-amber-500/15">
              <Coins size={24} className="text-amber-500" />
            </Box>
            <Box className="min-w-0">
              <Typography
                variant="h6"
                className="truncate font-semibold text-gray-900 dark:text-slate-100"
              >
                {tokenPack.name}
              </Typography>
              <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
                Code: {tokenPack.code}
              </Typography>
            </Box>
          </Box>

          <Box className="flex flex-col items-end gap-1">
            <Switch
              checked={tokenPack.isActive}
              onChange={(event) => onToggleStatus(tokenPack.publicId, event.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#f59e0b',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#f59e0b',
                },
              }}
            />
            <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
              {tokenPack.isActive ? 'Active' : 'Inactive'}
            </Typography>
          </Box>
        </Box>

        <Box className="mb-4">
          <Typography
            variant="subtitle2"
            className="mb-2 font-semibold text-gray-700 dark:text-slate-300"
          >
            Prices
          </Typography>
          <Box className="space-y-2">
            {tokenPack.prices.map((price) => (
              <Box
                key={price.currency}
                className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-700/50"
              >
                <span className="text-sm font-semibold text-gray-700 dark:text-slate-200">
                  {price.currency}
                </span>
                <span className="text-sm font-bold text-gray-900 dark:text-white">
                  {formatPrice(price.price, price.currency)}
                </span>
              </Box>
            ))}
          </Box>
          <Box className="mt-2 flex flex-wrap items-center gap-2">
            <Chip
              label={`${formatAdminNumber(tokenPack.tokenAmount)} credits`}
              size="small"
              sx={{
                backgroundColor: '#10B98120',
                color: '#10B981',
                fontWeight: 600,
              }}
            />
            <Chip
              label={`Sort: ${formatAdminNumber(tokenPack.sortOrder)}`}
              size="small"
              sx={{
                backgroundColor: '#3B82F620',
                color: '#3B82F6',
                fontWeight: 600,
              }}
            />
          </Box>
        </Box>

        <Divider className="my-4" />

        <Box className="mb-4 rounded-lg bg-slate-50 px-3 py-2 dark:bg-slate-700/50">
          <Typography variant="body2" className="font-medium text-gray-900 dark:text-slate-100">
            {formatAdminNumber(tokenPack.tokenAmount)} advance tokens
          </Typography>
          <Typography variant="caption" className="text-gray-500 dark:text-slate-400">
            Credited to user wallet after purchase
          </Typography>
        </Box>

        <Button
          fullWidth
          variant="outlined"
          onClick={() => onEdit(tokenPack.publicId)}
          sx={{
            borderColor: '#f59e0b',
            color: '#d97706',
            textTransform: 'none',
            fontWeight: 600,
            py: 1,
            '&:hover': {
              borderColor: '#d97706',
              backgroundColor: '#f59e0b10',
            },
          }}
        >
          Edit Token Pack
        </Button>
      </Box>
    </Box>
  );
};
